// src/components/ChangePasswordDialog.js - FIXED VERSION

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  LockReset,
  Password,
  CheckCircle,
  RadioButtonUnchecked,
  Close,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import api from '../../services/api';

const ChangePasswordDialog = ({ open, onClose, onSuccess }) => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const inputBg = isDark ? '#121212' : '#FFFFFF';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    
    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers,
      requirements: {
        minLength,
        hasUpperCase,
        hasLowerCase,
        hasNumbers
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError('New password does not meet requirements');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.data.success) {
        setSuccess('Password changed successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        if (onSuccess) onSuccess();
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.details || 
                          'Failed to change password';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = validatePassword(formData.newPassword);

  const inputStyle = {
    '& .MuiInputBase-root': {
      height: 32,
      fontSize: '0.8rem',
      color: textColorPrimary,
      bgcolor: inputBg,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: cardBg,
          color: textColorPrimary,
          borderRadius: '12px',
          border: cardBorder,
          width: { xs: '95vw', sm: '440px' },
          maxWidth: '460px !important',
          boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.25, pb: 1, borderBottom: cardBorder, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LockReset color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.95rem' }}>
            Change Password
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={loading} sx={{ p: '3px', color: textColorSecondary }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1, py: 0.25, px: 1.25, fontSize: '0.75rem' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 1, py: 0.25, px: 1.25, fontSize: '0.75rem' }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Current Password"
            name="currentPassword"
            type={showCurrentPassword ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={handleChange}
            disabled={loading}
            required
            InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
            sx={inputStyle}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ fontSize: 16 }} color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showCurrentPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="New Password"
            name="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={handleChange}
            disabled={loading}
            required
            InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
            sx={inputStyle}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Password sx={{ fontSize: 16 }} color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            size="small"
            margin="dense"
            label="Confirm New Password"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            disabled={loading}
            required
            error={Boolean(
              formData.confirmPassword && 
              formData.newPassword !== formData.confirmPassword
            )}
            helperText={
              formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                ? 'Passwords do not match'
                : ''
            }
            FormHelperTextProps={{ sx: { fontSize: '0.68rem', mx: 0.5, mt: 0.2 } }}
            InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
            sx={inputStyle}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Password sx={{ fontSize: 16 }} color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* Password Requirements Compact 2x2 Grid */}
          <Box sx={{ mt: 1, p: 1, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC', borderRadius: '8px', border: cardBorder }}>
            <Typography variant="caption" sx={{ color: textColorSecondary, fontWeight: 700, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
              Requirements:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
              {[
                { label: '≥ 6 characters', valid: passwordValidation.requirements.minLength },
                { label: 'One lowercase', valid: passwordValidation.requirements.hasLowerCase },
                { label: 'One uppercase', valid: passwordValidation.requirements.hasUpperCase },
                { label: 'One number', valid: passwordValidation.requirements.hasNumbers },
              ].map((req, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {formData.newPassword ? (
                    req.valid ? (
                      <CheckCircle sx={{ fontSize: 13, color: '#10B981' }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ fontSize: 13, color: textColorSecondary }} />
                    )
                  ) : (
                    <RadioButtonUnchecked sx={{ fontSize: 13, color: textColorSecondary, opacity: 0.5 }} />
                  )}
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: req.valid && formData.newPassword ? '#10B981' : textColorSecondary }}>
                    {req.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder }}>
          <Button 
            onClick={onClose} 
            disabled={loading}
            sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            variant="contained"
            startIcon={<LockReset sx={{ fontSize: 16 }} />}
            sx={{
              height: 30,
              fontSize: '0.8rem',
              borderRadius: '6px',
              textTransform: 'none',
              px: 2,
              bgcolor: '#0D9488',
              '&:hover': { bgcolor: '#0F766E' }
            }}
          >
            {loading ? 'Saving...' : 'Change Password'}
          </Button>
        </DialogActions>

        {loading && <LinearProgress sx={{ height: 3 }} />}
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;